import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { MissionDescriptionSkin } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';

import { useAuth } from '../../../../../shared/auth/state';
import { TradfiBoostBanner } from '../../../banner/tradfi-boost';
import { rewardHubV2Bunja } from '../../../state';
import { RegularMission } from '../../../types';
import {
  DistributionDate,
  ExpiredTime,
  MissionStatusBadges,
  MissionTitle,
  ProgressBar,
  ProgressBarItem,
  SectionDescriptionTooltipButton,
} from '../../misc';
import { MissionCtaButton } from '../../mission-cta-button';
import {
  getDaysUntilExpiration,
  getIsDepositAmountCondition,
  getIsTradingVolumeMission,
  getLocalizedText,
  getMissionStatusFlags,
  getProgressParams,
  getRewardAmount,
  getRewardCurrency,
  isProgressBarType,
} from '../../utils';
import { getSingleMissionCoinIconConfig } from './coin-icon-config';
import { SingleMissionCard, SingleMissionReward, SingleMissionTitleContent } from './misc';

interface Props {
  mission: RegularMission;
  sectionDescription?: MissionDescriptionSkin;
}

const SingleMission = ({ mission, sectionDescription }: Props) => {
  const { user } = useAuth();
  const locale = useSupportedLocale();
  const { feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const status = mission.status;
  const skin = mission.skin;
  const spec = mission.spec;

  const isKycVerificationCondition = spec.completionConditions.some((condition) => condition === 'kycVerification');
  const validityConditions = spec.validityConditions;
  const daysUntilExpiration = getDaysUntilExpiration(validityConditions, user?.createdAt);

  const completionReward = spec.completionRewards[0];

  const { isAssigned, isExpired, isValid, isComplete, isClaimable, isRejected } = getMissionStatusFlags(status);
  const hasMultiProgress = Boolean(status?.progress?.length && status.progress.length > 1);
  const sectionTitle = getLocalizedText(sectionDescription?.head?.localizationTable, locale);
  const tooltipText = getLocalizedText(sectionDescription?.tail?.localizationTable, locale);
  const missionTitle = getLocalizedText(skin?.body?.localizationTable, locale);
  const isInProgress = isAssigned && isValid && !isExpired;
  const showExpiredTime = isInProgress && daysUntilExpiration;

  const progressType = status.progress[0].type;
  const isTradingVolumeMission = getIsTradingVolumeMission(spec.completionConditions);
  const rewardAmount = getRewardAmount(completionReward, feeVoucherRewardRecord);
  const rewardCurrency = getRewardCurrency(completionReward);
  const coinSrc = getSingleMissionCoinIconConfig(status).src;

  return (
    <li>
      {sectionTitle && (
        <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5', mb: 16 })}>{sectionTitle}</p>
      )}
      {showExpiredTime && <ExpiredTime daysUntilExpiration={daysUntilExpiration ?? 0} />}
      {/* tablet+ */}
      <div className={css({ display: { base: 'none', tablet: 'block' } })}>
        <SingleMissionCard showExpiredTime={Boolean(showExpiredTime)}>
          <SingleMissionReward
            rewardAmount={rewardAmount}
            rewardCurrency={rewardCurrency}
            isInProgress={isInProgress}
            coinSrc={coinSrc}
          />
          <div className={stack({ flex: 1 })}>
            <div className={css({ mb: { tablet: 8 } })}>
              <MissionStatusBadges isExpired={isExpired} isRejected={isRejected} />
            </div>
            <div
              className={hstack({
                gap: 2,
                textStyle: 'Text/L/Strong',
                color: isInProgress ? 'content.default.level-5' : 'content.default.level-1',
              })}
            >
              <div className={hstack({ flex: 1, gap: 2 })}>
                <SingleMissionTitleContent
                  isExpired={isExpired}
                  progressType={progressType}
                  isInProgress={isInProgress}
                  missionTitle={missionTitle}
                />
                {tooltipText && <SectionDescriptionTooltipButton text={tooltipText} />}
              </div>
              {(isKycVerificationCondition || (isClaimable && hasMultiProgress)) && (
                <MissionCtaButton
                  key={progressType}
                  missionIndex={mission.missionIndex}
                  sectionIndex={mission.sectionIndex}
                  completionReward={completionReward}
                  missionStatus={status}
                  isActive={mission.isActive}
                  progress={status.progress[0]}
                  css={{ minW: 66 }}
                />
              )}
            </div>
            <div
              className={flex({
                flexDir: { base: 'column', smallDesktop: 'row' },
                flex: 1,
                gap: { base: 8, smallDesktop: 64 },
                mt: hasMultiProgress ? 16 : 0,
              })}
            >
              {isInProgress &&
                status?.progress?.filter(isProgressBarType).map((item) => {
                  const { target, current } = getProgressParams(item);
                  return (
                    <div
                      key={item.type}
                      className={hstack({
                        flex: 1,
                        gap: 24,
                        '& + &': { mt: { tablet: 16, smallDesktop: 0 } },
                      })}
                    >
                      <div className={stack({ flex: 1 })}>
                        {hasMultiProgress && (
                          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-3' })}>
                            {item.type === 'DepositAmount' ? (
                              <FormattedMessage id='Reward_hub.r250213.Deposit' />
                            ) : (
                              <FormattedMessage id='Reward_hub.r250213.Trade' />
                            )}
                          </p>
                        )}
                        <ProgressBar total={target} current={current} />
                      </div>
                      {(!isComplete || (isClaimable && !hasMultiProgress)) && (
                        <MissionCtaButton
                          key={item.type}
                          missionIndex={mission.missionIndex}
                          sectionIndex={mission.sectionIndex}
                          completionReward={completionReward}
                          missionStatus={status}
                          isActive={mission.isActive}
                          progress={item}
                          css={{ minW: 80 }}
                        />
                      )}
                    </div>
                  );
                })}
            </div>
            <DistributionDate
              date={status.settleAt}
              showMaintainMessage={spec.completionConditions.some(getIsDepositAmountCondition)}
              isHide={isExpired || isRejected}
            />
            {isTradingVolumeMission && <TradfiBoostBanner css={{ mt: 16 }} />}
          </div>
        </SingleMissionCard>
      </div>
      {/* base (mobile) */}
      <div className={css({ display: { base: 'block', tablet: 'none' } })}>
        <SingleMissionCard showExpiredTime={Boolean(showExpiredTime)}>
          <SingleMissionReward
            rewardAmount={rewardAmount}
            rewardCurrency={rewardCurrency}
            isInProgress={isInProgress}
            tooltipSlot={tooltipText ? <SectionDescriptionTooltipButton text={tooltipText} /> : undefined}
          />
          <MissionTitle
            title={
              <div className={hstack({ gap: 8 })}>
                <MissionStatusBadges isExpired={isExpired} isRejected={isRejected} />
                <SingleMissionTitleContent
                  isExpired={isExpired}
                  progressType={progressType}
                  isInProgress={isInProgress}
                  missionTitle={missionTitle}
                />
              </div>
            }
            missionIndex={mission.missionIndex}
            sectionIndex={mission.sectionIndex}
            completionReward={completionReward}
            status={status}
            isActive={mission.isActive}
          />
          <ProgressBarItem status={status} isHide={!isInProgress} />
          <DistributionDate
            date={status.settleAt}
            showMaintainMessage={spec.completionConditions.some(getIsDepositAmountCondition)}
            isHide={isExpired || isRejected}
          />
          {isTradingVolumeMission && <TradfiBoostBanner css={{ mt: 16 }} />}
        </SingleMissionCard>
      </div>
    </li>
  );
};

export default SingleMission;
